import { getField, updateField } from "vuex-map-fields";
import { VuexModule, Module, Action } from "vuex-module-decorators";
import { 
  createLabels, 
  createChartData 
} from "./expanded-dashboard.utils";
import ExpandedDashboardService from "./expanded-dashboard.service";
import moment, { Moment } from "moment";
import { 
  Options,
  ChartData,
  Assessment,
  AlertZone,
  AssessmentTypes
} from "./expanded-dashboard.types";

@Module({ namespaced: true })
class ExpandedDashboard extends VuexModule {
  phq9ChartData: ChartData = new ChartData();
  gad7ChartData: ChartData = new ChartData();
  sowsChartData: ChartData = new ChartData();
  cravingsChartData: ChartData = new ChartData();
  phq9ChartOptions: Options = new Options();
  gad7ChartOptions: Options = new Options();
  sowsChartOptions: Options = new Options();
  cravingsChartOptions: Options = new Options();
  phq9Assessment: Assessment = new Assessment();
  gad7Assessment: Assessment = new Assessment();
  sowsAssessment: Assessment = new Assessment();
  cravingsAssessment: Assessment = new Assessment();
  phq9AlertZones: AlertZone[] = [];
  gad7AlertZones: AlertZone[] = [];
  sowsAlertZones: AlertZone[] = [];
  cravingsAlertZones: AlertZone[] = [];
  labels: string[][] = [];
  startDate: Moment = moment();
  endDate: Moment = moment();

  private _initialState!: Record<string, any>;

  constructor(module: any) {
    super(module);
    this._initialState = { ...this };
  }

  @Action({ rawError: true })
  async fetchLabels(date: Date) {
    const startDate = moment(date).endOf('day').add(-12, 'weeks');
    this.context.commit("updateField", {
      path: "startDate",
      value: startDate
    });
    const endDate = moment(date).endOf('day');
    this.context.commit("updateField", {
      path: "endDate",
      value: endDate
    });
    const labels: string[][] = createLabels(startDate, endDate);
    this.context.commit("updateField", {
      path: "labels",
      value: labels
    });
  }

  @Action({ rawError: true })
  async fetchPhq9ChartOptions(clientId: number) {
    const phq9AlertZones = await ExpandedDashboardService.getAlertZones(AssessmentTypes.PHQ9, clientId);
    this.context.commit("updateField", {
      path: "phq9AlertZones",
      value: phq9AlertZones
    });
  }

  @Action({ rawError: true })
  async fetchGad7ChartOptions(clientId: number) {
    const gad7AlertZones = await ExpandedDashboardService.getAlertZones(AssessmentTypes.GAD7, clientId);
    this.context.commit("updateField", {
      path: "gad7AlertZones",
      value: gad7AlertZones
    });
  }

  @Action({ rawError: true })
  async fetchSowsChartOptions(clientId: number) {
    const sowsAlertZones = await ExpandedDashboardService.getAlertZones(AssessmentTypes.SOWS, clientId);
    this.context.commit("updateField", {
      path: "sowsAlertZones",
      value: sowsAlertZones
    });
  }

  @Action({ rawError: true })
  async fetchCravingsChartOptions(clientId: number) {
    const cravingsAlertZones = await ExpandedDashboardService.getAlertZones(AssessmentTypes.Cravings, clientId);
    this.context.commit("updateField", {
      path: "cravingsAlertZones",
      value: cravingsAlertZones
    });
  }

  @Action({ rawError: true })
  async fetchPhq9ChartData({ participantId, clientId, timezone }: { participantId: number, clientId: number, timezone: string }) {
    const startDate = this.startDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const endDate = this.endDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const data = await ExpandedDashboardService.getDataset(startDate, endDate, participantId, clientId, timezone, AssessmentTypes.PHQ9);
    const phq9ChartData: ChartData = createChartData(this.labels, data.dataSet, data.dataPoints);
    this.context.commit("updateField", {
      path: "phq9ChartData",
      value: phq9ChartData
    });
    const phq9Assessment: Assessment = {
      high: data.high,
      low: data.low,
      median: data.median
    }
    this.context.commit("updateField", {
      path: "phq9Assessment",
      value: phq9Assessment
    });
  }

  @Action({ rawError: true })
  async fetchGad7ChartData({ participantId, clientId, timezone }: { participantId: number, clientId: number, timezone: string }) {
    const startDate = this.startDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const endDate = this.endDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const data = await ExpandedDashboardService.getDataset(startDate, endDate, participantId, clientId, timezone, AssessmentTypes.GAD7);
    const gad7ChartData: ChartData = createChartData(this.labels, data.dataSet, data.dataPoints);
    this.context.commit("updateField", {
      path: "gad7ChartData",
      value: gad7ChartData
    });
    const gad7Assessment: Assessment = {
      high: data.high,
      low: data.low,
      median: data.median
    }
    this.context.commit("updateField", {
      path: "gad7Assessment",
      value: gad7Assessment
    });
  }

  @Action({ rawError: true })
  async fetchSowsChartData({ participantId, clientId, timezone }: { participantId: number, clientId: number, timezone: string }) {
    const startDate = this.startDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const endDate = this.endDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const data = await ExpandedDashboardService.getDataset(startDate, endDate, participantId, clientId, timezone, AssessmentTypes.SOWS);
    const sowsChartData: ChartData = createChartData(this.labels, data.dataSet, data.dataPoints);
    this.context.commit("updateField", {
      path: "sowsChartData",
      value: sowsChartData
    });
    const sowsAssessment: Assessment = {
      high: data.high,
      low: data.low,
      median: data.median
    }
    this.context.commit("updateField", {
      path: "sowsAssessment",
      value: sowsAssessment
    });
  }

  @Action({ rawError: true })
  async fetchCravingsChartData({ participantId, clientId, timezone }: { participantId: number, clientId: number, timezone: string }) {
    const startDate = this.startDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const endDate = this.endDate.format("YYYY-MM-DD[T]HH:mm:ss");
    const data = await ExpandedDashboardService.getDataset(startDate, endDate, participantId, clientId, timezone);
    const cravingsChartData: ChartData = createChartData(this.labels, data.dataSet, data.dataPoints);
    this.context.commit("updateField", {
      path: "cravingsChartData",
      value: cravingsChartData
    });
    const cravingsAssessment: Assessment = {
      high: data.high,
      low: data.low,
      median: data.median
    }
    this.context.commit("updateField", {
      path: "cravingsAssessment",
      value: cravingsAssessment
    });
  }

  @Action({ rawError: true })
  resetState() {
    for (const key of Object.keys(this._initialState)) {
      this.context.commit("updateField", {
        path: key,
        value: this._initialState[key]
      });
    }
  }
}



ExpandedDashboard.getters = { getField };
ExpandedDashboard.mutations = { updateField };

export default ExpandedDashboard;